import { useCallback, useContext, useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { TOContext } from "context/TContext";
import { formatNumber, priceTransform } from "helpers/formatNumber";
import { getSuggestedTipAmount } from "helpers/getSuggestedTipAmount";

export const useTips = () => {
  const theme = useTheme();
  const [hasChange, setHasChange] = useState(false);
  const [hasUserInteraction, setHasUserInteraction] = useState(false);
  const [tipValue, setTipValue] = useState("0.00");
  const [lastAppliedTip, setLastAppliedTip] = useState(null);
  const {
    selectedTip,
    setSelectedTip,
    charges,
    setCharges,
    aggregatedTipOptions,
  } = useContext(TOContext);

  const updateChargesSafely = useCallback(
    (tipAmount) => {
      setCharges((prevCharges) => ({
        ...prevCharges,
        tipInfo: {
          ...prevCharges?.tipInfo,
          kitchen: Number(tipAmount) || 0,
        },
        balanceDue:
          prevCharges?.balanceDue -
          (prevCharges?.tipInfo?.kitchen || 0) +
          (Number(tipAmount) || 0),
        total:
          prevCharges?.total -
          (prevCharges?.tipInfo?.kitchen || 0) +
          (Number(tipAmount) || 0),
      }));
    },
    [setCharges],
  );

  const handleSelectTip = (item) => {
    const numericAmount = getSuggestedTipAmount(
      charges?.subTotal,
      item,
      aggregatedTipOptions?.kitchenTip,
    );
    setSelectedTip(item);
    const formattedValue = formatNumber(numericAmount);
    setTipValue(formattedValue || "0.00");
    setHasUserInteraction(true);
    // Auto-apply to charges so it's visible immediately in Charges component
    updateChargesSafely(numericAmount);
  };

  useEffect(() => {
    // Don't override an explicit tip selection the user has made
    if (hasUserInteraction) return;

    if (
      !charges?.tipInfo?.kitchen ||
      charges?.tipInfo?.kitchen === 0 ||
      charges?.tipInfo?.kitchen === 0.0
    ) {
      if (aggregatedTipOptions?.kitchenTip && charges?.subTotal) {
        const defaultTip =
          aggregatedTipOptions.kitchenTip[
            aggregatedTipOptions.defaultSelectedKitchenIndex
          ];
        // Only auto-apply the default preset on the very first load (before
        // the user has confirmed any tip). Once lastAppliedTip is non-null the
        // user has confirmed a value (even 0) and we must not override it.
        if (defaultTip && lastAppliedTip === null) {
          const numericAmountParse = getSuggestedTipAmount(
            charges?.subTotal,
            defaultTip,
            aggregatedTipOptions?.kitchenTip,
          );
          const convertedTip = formatNumber(numericAmountParse);

          setTipValue(convertedTip || "0.00");
          setSelectedTip(defaultTip);
          if (
            numericAmountParse > 0 &&
            charges?.tipInfo?.kitchen !== numericAmountParse
          ) {
            updateChargesSafely(numericAmountParse);
            setLastAppliedTip(numericAmountParse);
          }
        }
      }
      return;
    }
    if (
      charges &&
      charges?.tipInfo &&
      charges?.tipInfo?.kitchen &&
      aggregatedTipOptions?.kitchenTip
    ) {
      const chargesTipRounded =
        Number(charges?.tipInfo?.kitchen?.toFixed(2)) || 0;
      const matchingTipOption = aggregatedTipOptions.kitchenTip.find((tip) => {
        const suggested = getSuggestedTipAmount(
          charges?.subTotal,
          tip,
          aggregatedTipOptions?.kitchenTip,
        );
        return Math.abs(suggested - chargesTipRounded) < 0.01;
      });
      if (matchingTipOption) {
        setSelectedTip(matchingTipOption);
        const numericAmount = getSuggestedTipAmount(
          charges?.subTotal,
          matchingTipOption,
          aggregatedTipOptions?.kitchenTip,
        );
        setTipValue(formatNumber(numericAmount) || "0.00");
      } else {
        setTipValue(formatNumber(charges?.tipInfo?.kitchen) || "0.00");
      }
    } else {
      if (aggregatedTipOptions?.kitchenTip) {
        const defaultTip =
          aggregatedTipOptions.kitchenTip[
            aggregatedTipOptions.defaultSelectedKitchenIndex
          ];
        const numericAmountParse = getSuggestedTipAmount(
          charges?.subTotal,
          defaultTip,
          aggregatedTipOptions?.kitchenTip,
        );
        const convertedTip = formatNumber(numericAmountParse);

        setTipValue(convertedTip || "0.00");
        setSelectedTip(defaultTip);
        // Don't update charges automatically - user must click ADD to apply
      }
    }
  }, [
    hasUserInteraction,
    lastAppliedTip,
    charges,
    charges?.tipInfo?.kitchen,
    charges?.subTotal,
    aggregatedTipOptions,
    updateChargesSafely,
    setSelectedTip,
  ]);

  const tipStyleMemo = useCallback(
    (tipId) => {
      const activeTip = `2px solid ${theme.palette.warning.main}`;
      const inActiveTip = `1px solid ${theme.palette.grey[900]}`;
      return selectedTip?.id === tipId ? activeTip : inActiveTip;
    },
    [selectedTip?.id, theme.palette.grey, theme.palette.warning.main],
  );

  const handleChangeTip = (event) => {
    const value = priceTransform(event.target.value);
    if (value !== null) {
      setTipValue(formatNumber(value) || "0.00");
      setHasUserInteraction(true);
    }
  };

  // Track the initial/last applied tip value from charges.
  // Only initialise from the backend when the server already has a non-zero
  // kitchen tip, so that a 0-tip order keeps lastAppliedTip === null and lets
  // the main effect perform its one-time default-preset auto-apply.
  useEffect(() => {
    if (charges?.tipInfo?.kitchen !== undefined) {
      const initialTip = Number(charges?.tipInfo?.kitchen?.toFixed(2)) || 0;
      if (lastAppliedTip === null && initialTip > 0) {
        setLastAppliedTip(initialTip);
      }
    }
  }, [charges?.tipInfo?.kitchen, lastAppliedTip]);

  // Update hasChange when tipValue differs from last applied tip
  useEffect(() => {
    const numericTipValue = parseFloat(tipValue.replace(/,/g, "")) || 0;
    const numericLastAppliedTip = lastAppliedTip !== null ? lastAppliedTip : 0;
    const hasDifference =
      Math.abs(numericTipValue - numericLastAppliedTip) > 0.01;
    setHasChange(hasDifference);
  }, [tipValue, lastAppliedTip]);

  const handleClick = () => {
    const numericAmount = parseFloat(tipValue.replace(/,/g, "")) || 0;
    updateChargesSafely(numericAmount);
    setLastAppliedTip(numericAmount);
    setSelectedTip(null);
    setHasUserInteraction(false);
    // hasChange will be automatically updated by the comparison useEffect
  };

  return {
    tipStyleMemo,
    handleSelectTip,
    tipValue,
    selectedTip,
    loadingApplyTip: false,
    aggregatedTipOptions,
    handleChangeTip,
    handleClick,
    hasChange,
    hasUserInteraction,
    manualLoadingTip: false,
  };
};
